import { put, call, takeLatest, select, take, actionChannel, takeEvery } from "redux-saga/effects";
import * as Api from "../api";
import * as ActionConstants from "../actions/actionConstants";
import { SHOW_LOADER, HIDE_LOADER } from '../../../common/actionConstants';
import {parseDateWithUSCheck, getAxiosError} from '../../../common/util';
import { logout } from './common';
import { ErrorCode, noteFilterConstants, CombinedViewSettingTypes } from "../constants";
import { combinedViewDateKey } from "../CombinedView/dateKey";
import { hydrateUsers } from '../../../common/sagas/userListSaga';
import { getOrgs } from '../../../common/sagas/orgListSaga';
import * as PUBLIC_API_NOTE_ACTIONS from "../../../publicApiActions/noteActions";

/**
 * Joins an id collection into the comma string the /cvp query string expects.
 * Accepts an array of ids, an array of objects with an id-bearing key, or an
 * already-joined string, because the callers hold each of those shapes.
 */
function toIdList(value, key) {
  if (value == null) return ""
  if (typeof value === "string") return value
  if (!Array.isArray(value)) return String(value)
  return value
    .map((item) => (item && typeof item === "object" ? item[key] : item))
    .filter((id) => id != null && id !== "")
    .join(",")
}

export function settingsFromResponse(cvgs) {
  const enabled = new Set((cvgs && cvgs.enabled_types) || [])
  const settings = { IsSortedAscending: !!(cvgs && cvgs.ascending) }
  Object.keys(CombinedViewSettingTypes).forEach((flag) => {
    settings[flag] = CombinedViewSettingTypes[flag].some((type) => enabled.has(type))
  })
  return settings
}

/**
 * Builds the /cvp query object.
 *
 * Every generator that refetches the feed needs the identical parameter set,
 * differing only in which activity/user selection and which settings object is
 * current. Keeping one builder stops the six copies from drifting apart, which
 * is how the legacy `actions: true` parameter survived long after the server
 * stopped accepting it.
 */
export function buildCombinedViewParams({
  workkardId,
  settings,
  activityIds,
  userIds,
  start,
  end,
  sdate = "",
  edate = "",
  dntids = "",
  ascending
}) {
  const content = settings || {}
  return {
    id: workkardId,
    aids: toIdList(activityIds, "Id"),
    ids: toIdList(userIds, "UserId"),
    start,
    end,
    sort: ascending ? "a" : "d",
    // The creation markers follow the module umbrella: the legacy feed showed
    // "Workkard created"/"Activity created" whenever any content type for that
    // module was on, and neither has a checkbox of its own.
    notes: !!(content.WorkkardNotes || content.WorkkardLogs || content.WorkkardActions || content.WorkkardFiles),
    activities: !!(content.ActivityNotes || content.ActivityLogs || content.ActivityActions || content.ActivityFiles),
    logs: !!content.WorkkardLogs,
    wkact: !!content.WorkkardActions,
    files: !!content.WorkkardFiles,
    anotes: !!content.ActivityNotes,
    alogs: !!content.ActivityLogs,
    aactions: !!content.ActivityActions,
    afiles: !!content.ActivityFiles,
    sdate,
    edate,
    dntids: toIdList(dntids)
  }
}

/**
 * Buckets a page's attachments by day so the gallery and the per-date file
 * strip can index them.
 *
 * The key is derived here from the entry's timestamp rather than read off a
 * server-formatted string, so the grouping follows the viewer's locale and
 * cannot drift from the date headers rendered beside it.
 */
export function* saveCombinedViewfiles(cvp, isUpdate) {
  let fileData = {}
  let galleryData = []
  const entries = (cvp && cvp.entries) || []

  entries.forEach((entry) => {
    const files = (entry && entry.files) || []
    if (files.length === 0) return
    const bucket = combinedViewDateKey(entry.occurred_at)
    const existing = fileData[bucket] || []
    const merged = [].concat(existing, files)
    fileData = Object.assign({}, fileData, { [bucket]: merged })
    galleryData = [].concat(galleryData, [files.filter((file) => file.file_type === "Image")])
  })

  yield put({
    type: isUpdate
      ? ActionConstants.UPDATE_COMBINED_VIEW_FILES_SUCCESS
      : ActionConstants.FETCH_COMBINED_VIEW_FILES_SUCCESS,
    payload: {
      fileData: fileData,
      galleryData: galleryData.filter((group) => group.length > 0)
    }
  })
}

/**
 * Hydrates the shared user and org stores for the ids a page references.
 *
 * The feed carries ids rather than names so that a user seen on another tab is
 * not refetched, and so that actors outside the caller's organization - a
 * partner-org assignee on a years-old transfer - still resolve.
 */
function* hydrateCombinedViewActors(cvp, ...extraOrgIds) {
  if (!cvp) return
  yield call(hydrateUsers, cvp.referenced_user_ids || [])
  yield call(getOrgs, [].concat(cvp.referenced_org_ids || [], extraOrgIds.filter(Boolean)))
}

export function* fetchCombinedViewData({payload}){
    try{
      yield put({ type: SHOW_LOADER });
      const getCombinedViewData = (state) => state.workkardDetail.combinedView;
      const combinedViewData=yield select(getCombinedViewData);
      let fetchdata = payload.fetchdata;      
      if (!fetchdata){
        let cvfilters = {}
        const cvfat = yield call(Api.getCombinedViewDatacvfatCall, ActionConstants.GET_COMBINED_VIEW_ACTIVITY_SUCCESS, payload)
        const activityOptions = cvfat.activities || []
        activityOptions.forEach((e) => {
          if (combinedViewData.cvExcludedFilters[e.template_short_name] == null) {
            cvfilters[e.template_short_name] = combinedViewData.cvExcludedFilters[0];
          } else {
            cvfilters[e.template_short_name] = combinedViewData.cvExcludedFilters[e.template_short_name];
          }
        });
        const cvfu=yield call(Api.getCombinedViewDatacvfuCall,ActionConstants.GET_COMBINED_VIEW_FILTER_SUCCESS,payload)
        const cvgs =yield call(Api.getCombinedViewDatacvgsCall,ActionConstants.GET_COMBINED_VIEW_SETTING_SUCCESS,null)
        // The user filter lists ids only; the labels come from the shared user
        // store, which this also warms so the options render immediately.
        const userOptions = (cvfu.user_ids || []).map((id) => ({ UserId: id }))
        yield call(hydrateUsers, cvfu.user_ids || [])
        const settings = settingsFromResponse(cvgs)
        yield put({ type: ActionConstants.RESET_COMBINED_VIEW_DATA })
        const settingsdata={
          activity:[],
          users:[],
          filters:{users:userOptions,activityTitle:activityOptions,contentFilter:settings},
          cvExcludedFilters: cvfilters,
          settings:settings
        }
        yield put ({
          type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
          payload: settingsdata
        })
      } else {
        const datacvp = buildCombinedViewParams({
          workkardId: payload.id,
          settings: combinedViewData.settings,
          activityIds: combinedViewData.activity,
          userIds: combinedViewData.users,
          start: payload.start,
          end: payload.end,
          ascending: combinedViewData.settings.IsSortedAscending,
          dntids: payload.dntids ? payload.dntids.toString() : combinedViewData.dntids
        })
        yield put({type: ActionConstants.RESET_COMBINED_VIEW_DATA})
        const cvp=yield call(Api.getCombinedViewDatacvpCall,ActionConstants.GET_COMBINE_VIEW_ALL_DATA_SUCCESS,datacvp)
        const data={
          activity:combinedViewData.activity,
          users:combinedViewData.users,
          filters:{isIgnore:true,users:combinedViewData.filters.users,activityTitle:combinedViewData.filters.activityTitle,contentFilter:combinedViewData.filters.contentFilter},
          settings:combinedViewData.settings,
          date:{sdate:"",edate:""},
          start:payload.start,
          end:payload.end,
          dntids: combinedViewData.dntids,
          showAllTemplates : combinedViewData.showAllTemplates
        }

        if (cvp.status == 200 && cvp.entries) {
          data.entries = cvp.entries;
          data.total = cvp.total;
          yield put ({
            type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
            payload: data
          })
          yield* saveCombinedViewfiles(cvp, false)
          yield* hydrateCombinedViewActors(cvp)
        }
        else if(cvp.status ==  ErrorCode.RedirectToLogout){
          yield* logout();
        } else {
            yield put({
            type: ActionConstants.LOG_APPLICATON_ERROR,
            payload: { status:cvp.status, isErrorScreen:false }
            });
        }
      }
    }
    catch(error){
  
    }
    finally{
      yield put({ type: HIDE_LOADER });      
    }
  }

export function* fetchCombinedViewWorkkardFiles({payload}){
  try{
    const getWorkkardDetailData = (state) => state.workkardDetail.workkardDetail;
    const getActivityListing = (state) => state.workkardDetail.activityListing;
    const workkardDetailData = yield select(getWorkkardDetailData);
    const activityListing = yield select(getActivityListing);
    const actIds = (activityListing.activities || []).map((activity) => activity.activityId).join(",");

    const files = yield call(Api.getCombinedViewWorkkardFilesCall, ActionConstants.FETCH_COMBINED_VIEW_WORKKARD_FILES_SUCCESS, {
      type: "All",
      cId: workkardDetailData.Id,
      actId: actIds,
      cvdownload: true
    });

    const seenGuids = new Set();
    const uniqueFiles = (Array.isArray(files) ? files : []).filter((file) => {
      if (seenGuids.has(file.FileGuid)) return false;
      seenGuids.add(file.FileGuid);
      return true;
    });

    yield put({
      type: ActionConstants.FETCH_COMBINED_VIEW_WORKKARD_FILES_SUCCESS,
      payload: uniqueFiles
    });
  }
  catch(error){

  }
}

export function* updateCombinedViewData({payload}){
  try{
    yield put({type:SHOW_LOADER});
      const getWorkkardDetailData=(state)=>state.workkardDetail.workkardDetail;
      const getCombinedViewData=(state)=>state.workkardDetail.combinedView;
      const workkardDetailData=yield select(getWorkkardDetailData);
      const combinedViewData=yield select(getCombinedViewData);
      const datacvp = buildCombinedViewParams({
        workkardId: workkardDetailData.Id,
        settings: combinedViewData.settings,
        activityIds: combinedViewData.activity,
        userIds: combinedViewData.users,
        start: payload.start,
        end: payload.end,
        ascending: combinedViewData.settings.IsSortedAscending,
        sdate: combinedViewData.date.sdate,
        edate: combinedViewData.date.edate,
        dntids: combinedViewData.showAllTemplates ? "" : combinedViewData.dntids
      })

      const cvp=yield call(Api.getCombinedViewDatacvpCall,ActionConstants.GET_COMBINE_VIEW_ALL_DATA_SUCCESS,datacvp)

      const data={
        entries:cvp.entries,
        total:cvp.total,
        start:payload.start,
        end:payload.end
      }
      yield put ({
        type: ActionConstants.UPDATE_COMBINED_VIEW_DATA_SUCCESS,
        payload: data
      })
      if (cvp.status == 200 && cvp.entries) {
        yield* saveCombinedViewfiles(cvp, true)
        yield* hydrateCombinedViewActors(cvp)
      }
      else if(cvp.status ==  ErrorCode.RedirectToLogout){
        yield* logout();
      } else {
          yield put({
          type: ActionConstants.LOG_APPLICATON_ERROR,
          payload: { status:cvp.status, isErrorScreen:false }
          });
      }
  }
  catch(error){

  }
  finally{
    yield put({type:HIDE_LOADER});
  }
}
  
  
  // The per-row lazy fetchers for files (/cvf), note custom fields (/cvncf),
  // log detail (/cvl) and profiles (/cvu) are gone: every page of the feed now
  // arrives with its attachments, custom fields, field changes and actor ids
  // already attached, so there is nothing left to fetch on expand.

  export function* fetchCombinedViewSettingsFilterData({payload}){
    try{
      yield put({type:SHOW_LOADER});
      const {status}=yield call(Api.saveCombinedViewSettings,ActionConstants.SETTINGS_FILTER_SUCCESS,payload.settings);
      if(status==200){
        const getWorkkardDetailData=(state)=>state.workkardDetail.workkardDetail;
        const getCombinedViewData=(state)=>state.workkardDetail.combinedView;
        const workkardDetailData=yield select(getWorkkardDetailData);
        const combinedViewData=yield select(getCombinedViewData);

        if((payload.sdate == "" || payload.edate=="") || (new Date(payload.sdate)<=new Date(payload.edate))){
          yield put({type: ActionConstants.RESET_COMBINED_VIEW_DATA})
          const datacvp = buildCombinedViewParams({
            workkardId: workkardDetailData.Id,
            settings: payload.settings,
            activityIds: combinedViewData.activity,
            userIds: combinedViewData.users,
            start: payload.start,
            end: payload.end,
            ascending: payload.settings.IsSortedAscending,
            sdate: payload.sdate,
            edate: payload.edate,
            dntids: combinedViewData.showAllTemplates ? "" : combinedViewData.dntids
          })
          const cvp=yield call(Api.getCombinedViewDatacvpCall,ActionConstants.GET_COMBINE_VIEW_ALL_DATA_SUCCESS,datacvp)

          const data={
            activity:combinedViewData.activity,
            // isignored flag is introduced to prevent duplicate call for cvp data need to fix later
            filters:{isIgnore:true,users:combinedViewData.filters.users,activityTitle:combinedViewData.filters.activityTitle,contentFilter:payload.settings},
            settings:Object.assign({},{Id:combinedViewData.settings.Id,UserId:combinedViewData.settings.UserId},payload.settings),
            entries:cvp.entries,
            total:cvp.total,
            users:combinedViewData.users,
            date:{sdate:payload.sdate,edate:payload.edate},
            start:payload.start,
            end:payload.end,
            dntids: combinedViewData.dntids,
            showAllTemplates : combinedViewData.showAllTemplates
          }
          yield put ({
            type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
            payload: data
          })
          if (cvp.status == 200 && cvp.entries) {
            yield* saveCombinedViewfiles(cvp, false)
            yield* hydrateCombinedViewActors(cvp)
          }
        }
        else{
          yield put({type: ActionConstants.RESET_COMBINED_VIEW_DATA})
        }
      }
      else if(status ==  ErrorCode.RedirectToLogout){
        yield* logout();
      } else {
          yield put({
          type: ActionConstants.LOG_APPLICATON_ERROR,
          payload: { status:status, isErrorScreen:false }
          });
      }
    }
    catch(error){

    }
    finally{
      yield put({type:HIDE_LOADER});
    }
  }

  export function* fetchCombinedViewUsersFilterData({payload}){
    try{
      yield put({type:SHOW_LOADER});

      const getWorkkardDetailData=(state)=>state.workkardDetail.workkardDetail;
      const getCombinedViewData=(state)=>state.workkardDetail.combinedView;

      const workkardDetailData=yield select(getWorkkardDetailData);
      const combinedViewData=yield select(getCombinedViewData);

      if((payload.sdate == "" || payload.edate=="") || (new Date(payload.sdate)<=new Date(payload.edate))){
        yield put({ type: ActionConstants.RESET_COMBINED_VIEW_DATA })
        const datacvp = buildCombinedViewParams({
          workkardId: workkardDetailData.Id,
          settings: combinedViewData.filters.contentFilter,
          activityIds: combinedViewData.activity,
          userIds: payload.users,
          start: payload.start,
          end: payload.end,
          ascending: combinedViewData.settings.IsSortedAscending,
          sdate: payload.sdate,
          edate: payload.edate,
          dntids: combinedViewData.showAllTemplates ? "" : combinedViewData.dntids
        })
        
        const updatedUsers = payload.users.map((id,key)=>{return combinedViewData.filters.users.filter(item=>item.UserId == id)}).map((users,index)=>{return users[0]}) 
        
        const cvp=yield call(Api.getCombinedViewDatacvpCall,ActionConstants.GET_COMBINE_VIEW_ALL_DATA_SUCCESS,datacvp)
  
        const data={
          activity:combinedViewData.activity,
          filters:{isIgnore:true,users:combinedViewData.filters.users,activityTitle:combinedViewData.filters.activityTitle,contentFilter:combinedViewData.filters.contentFilter},
          settings:combinedViewData.settings,
          entries:cvp.entries,
          total:cvp.total,
          users:updatedUsers,
          date:{sdate:payload.sdate,edate:payload.edate},
          start:payload.start,
          end:payload.end,
          dntids: combinedViewData.dntids,
          showAllTemplates : combinedViewData.showAllTemplates
        }
        yield put ({
          type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
          payload: data
        })
        if (cvp.status == 200 && cvp.entries) {
          yield* saveCombinedViewfiles(cvp, false)
          yield* hydrateCombinedViewActors(cvp)
        }
        else if(cvp.status ==  ErrorCode.RedirectToLogout){
          yield* logout();
        } else {
            yield put({
            type: ActionConstants.LOG_APPLICATON_ERROR,
            payload: { status:cvp.status, isErrorScreen:false }
            });
        }    
      }
      else{
        yield put({type: ActionConstants.RESET_COMBINED_VIEW_DATA})
      }
    }
    catch(error){

    }
    finally{
      yield put({type:HIDE_LOADER});
    }
  }

  export function* fetchCombinedViewActivityTitleFilterData({payload}){
    try{
      yield put({type:SHOW_LOADER});
      const getWorkkardDetailData=(state)=>state.workkardDetail.workkardDetail;
      const getCombinedViewData=(state)=>state.workkardDetail.combinedView;
  
      const workkardDetailData=yield select(getWorkkardDetailData);
      const combinedViewData=yield select(getCombinedViewData);

      if((payload.sdate == "" || payload.edate=="") || (new Date(payload.sdate)<=new Date(payload.edate))){
        yield put({ type: ActionConstants.RESET_COMBINED_VIEW_DATA })
        const datacvp = buildCombinedViewParams({
          workkardId: workkardDetailData.Id,
          settings: combinedViewData.filters.contentFilter,
          activityIds: payload.activityTitle,
          userIds: combinedViewData.users,
          start: payload.start,
          end: payload.end,
          ascending: combinedViewData.settings.IsSortedAscending,
          sdate: payload.sdate,
          edate: payload.edate,
          dntids: payload.dntids
        })
        const updatedActivityTitle = payload.activityTitle.map((id,key)=>{return combinedViewData.filters.activityTitle.filter(item=>item.Id == id)}).map((activity,index)=>{return activity[0]})  
        
        const cvp=yield call(Api.getCombinedViewDatacvpCall,ActionConstants.GET_COMBINE_VIEW_ALL_DATA_SUCCESS,datacvp)
  
        const data={
          activity:updatedActivityTitle,
          filters:{users:combinedViewData.filters.users,activityTitle:combinedViewData.filters.activityTitle,contentFilter:combinedViewData.filters.contentFilter},
          settings:combinedViewData.settings,
          entries:cvp.entries,
          total:cvp.total,
          users:combinedViewData.users,
          date:{sdate:payload.sdate,edate:payload.edate},
          start:payload.start,
          end:payload.end,
          dntids: payload.dntids.join(","),
          showAllTemplates : combinedViewData.showAllTemplates
        }
  
        yield put ({
          type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
          payload: data
        })
        if (cvp.status == 200 && cvp.entries) {
          yield* saveCombinedViewfiles(cvp, false)
          yield* hydrateCombinedViewActors(cvp)
        }
        else if(cvp.status ==  ErrorCode.RedirectToLogout){
          yield* logout();
        } else {
            yield put({
            type: ActionConstants.LOG_APPLICATON_ERROR,
            payload: { status:cvp.status, isErrorScreen:false }
            });
        }    
      }
      else{
        yield put({type: ActionConstants.RESET_COMBINED_VIEW_DATA})
      }
    }
    catch(error){
  
    }
    finally{
      yield put({type:HIDE_LOADER});
    }
  }
  
  export function* fetchCombinedViewDateFilterData({payload}){
    try{
      yield put({type:SHOW_LOADER});
      const getWorkkardDetailData=(state)=>state.workkardDetail.workkardDetail;
      const getCombinedViewData=(state)=>state.workkardDetail.combinedView;
  
      const workkardDetailData=yield select(getWorkkardDetailData);
      const combinedViewData=yield select(getCombinedViewData);

      if((payload.sdate == '' || payload.edate=='') || (new Date(payload.sdate)<=new Date(payload.edate))){
        yield put({type: ActionConstants.RESET_COMBINED_VIEW_DATA})
        const datacvp = buildCombinedViewParams({
          workkardId: workkardDetailData.Id,
          settings: combinedViewData.filters.contentFilter,
          activityIds: combinedViewData.activity,
          userIds: combinedViewData.users,
          start: payload.start,
          end: payload.end,
          ascending: combinedViewData.settings.IsSortedAscending,
          sdate: payload.sdate,
          edate: payload.edate,
          dntids: combinedViewData.showAllTemplates ? "" : combinedViewData.dntids
        })
        const cvp=yield call(Api.getCombinedViewDatacvpCall,ActionConstants.GET_COMBINE_VIEW_ALL_DATA_SUCCESS,datacvp)
        
        const data={
          activity:combinedViewData.activity,
          filters:{users:combinedViewData.filters.users,activityTitle:combinedViewData.filters.activityTitle,contentFilter:combinedViewData.filters.contentFilter},
          settings:combinedViewData.settings,
          entries:cvp.entries,
          total:cvp.total,
          users:combinedViewData.users,
          date:{sdate:payload.sdate,edate:payload.edate},
          start:payload.start,
          end:payload.end,
          dntids: combinedViewData.dntids,
          showAllTemplates : combinedViewData.showAllTemplates
        }
        yield put ({
          type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
          payload: data
        })
        if (cvp.status == 200 && cvp.entries) {
          yield* saveCombinedViewfiles(cvp, false)
          yield* hydrateCombinedViewActors(cvp)
        }
        else if(cvp.status ==  ErrorCode.RedirectToLogout){
          yield* logout();
        } else {
            yield put({
            type: ActionConstants.LOG_APPLICATON_ERROR,
            payload: { status:cvp.status, isErrorScreen:false }
            });
        }    
      }
      else{
        yield put({type: ActionConstants.RESET_COMBINED_VIEW_DATA})
      }
    }
    catch(error){
  
    }
    finally{
      yield put({type:HIDE_LOADER});
    }
  }

export function* sortCombinedViewData({payload}){
  try{
    yield put({type:SHOW_LOADER});

    const {status}=yield call(Api.saveCombinedViewSettings,ActionConstants.SETTINGS_FILTER_SUCCESS,payload.settings);

    if(status==200){
      const getWorkkardDetailData=(state)=>state.workkardDetail.workkardDetail;
      const getCombinedViewData=(state)=>state.workkardDetail.combinedView;

      const workkardDetailData=yield select(getWorkkardDetailData);
      const combinedViewData=yield select(getCombinedViewData);

      const datacvp = buildCombinedViewParams({
        workkardId: workkardDetailData.Id,
        settings: combinedViewData.filters.contentFilter,
        activityIds: combinedViewData.activity,
        userIds: combinedViewData.users,
        start: '0',
        end: '50',
        ascending: payload.settings.IsSortedAscending,
        sdate: combinedViewData.date.sdate,
        edate: combinedViewData.date.edate,
        dntids: combinedViewData.showAllTemplates ? "" : combinedViewData.dntids
      })
      yield put({ type: ActionConstants.RESET_COMBINED_VIEW_DATA })
      const cvp=yield call(Api.getCombinedViewDatacvpCall,ActionConstants.GET_COMBINE_VIEW_ALL_DATA_SUCCESS,datacvp);
      
      const data={
          activity:combinedViewData.activity,
          filters:{isIgnore:true,users:combinedViewData.filters.users,activityTitle:combinedViewData.filters.activityTitle,contentFilter:payload.settings},
          settings:Object.assign({},{Id:combinedViewData.settings.Id,UserId:combinedViewData.settings.UserId},payload.settings),
          entries:cvp.entries,
          total:cvp.total,
          users:combinedViewData.users,
          date:{sdate:combinedViewData.date.sdate,edate:combinedViewData.date.edate},
          start:'0',
          end:'50',
          dntids: combinedViewData.dntids,
          showAllTemplates : combinedViewData.showAllTemplates
      }
      yield put ({
        type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
        payload: data
      })
      if (cvp.status == 200 && cvp.entries) {
        yield* saveCombinedViewfiles(cvp, false)
        yield* hydrateCombinedViewActors(cvp)
      }
    }
    else if(status ==  ErrorCode.RedirectToLogout){
      yield* logout();
    } else {
        yield put({
        type: ActionConstants.LOG_APPLICATON_ERROR,
        payload: { status:status, isErrorScreen:false }
        });
    }
  }
  catch(error){

  }
  finally{
    yield put({type:HIDE_LOADER});
  }
}

export function* downloadAllFiles({payload}){
  try{
    yield put({type:SHOW_LOADER});
    const callback = payload.callback;
    delete payload.callback;
    let Data = yield call(Api.getWorkkardFilesData, ActionConstants.FETCH_MIDDLE_PANEL_WORKKARD_FILE_SUCCESS, payload);
    if (Data.status == 200 && Data.Total !== payload.take){
      payload.take = Data.Total;
      Data = yield call(Api.getWorkkardFilesData, ActionConstants.FETCH_MIDDLE_PANEL_WORKKARD_FILE_SUCCESS, payload);
    }
    if(Data.status==200){
      let fileUrls = [];
      Data.Data.map(file => (file.Files.map(item => fileUrls.push(item.FileUrl))))
      yield put ({
        type: ActionConstants.SET_ALL_FILES_FOR_DOWNLOAD,
        payload: fileUrls
      })
      callback();
    }
    else if(Data.status ==  ErrorCode.RedirectToLogout){
      yield* logout();
    } else {
        yield put({
        type: ActionConstants.LOG_APPLICATON_ERROR,
        payload: { status:Data.status, isErrorScreen:false }
        });
    }    
  }
  catch(error){

  }
  finally{
    yield put({type:HIDE_LOADER});
  }
}

export function* fetchPrintCombinedViewData({payload}){
  try{
    yield put({type:SHOW_LOADER});
    const cvp = yield call(Api.getPrintCombinedViewData, ActionConstants.FETCH_PRINT_COMBINED_VIEW_DATA_REQUEST, payload);
    if(cvp.status==200){
      const workkardDetailData = yield select((state)=>state.workkardDetail.workkardDetail);
      yield* hydrateCombinedViewActors(cvp, workkardDetailData.SponsorOrgId || workkardDetailData.OrganizationId)
      yield put ({
        type: ActionConstants.FETCH_PRINT_COMBINED_VIEW_DATA_SUCCESS,
        payload: cvp
      })
    }
    else if(cvp.status == ErrorCode.RedirectToLogout){
      yield* logout();
    } else {
      yield put({
        type: ActionConstants.LOG_APPLICATON_ERROR,
        payload: { status:cvp.status, isErrorScreen:false }
      });
    }
  }
  catch(error){

  }
  finally{
    yield put({type:HIDE_LOADER});
  }
}

export function* updatedeselectednotetemplates({ payload }) {
  try {
    yield put({
      type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
      payload: payload,
    });
  } catch (error) {}
}

export function* savedeselectednotetemplates({payload}) {
  try {
    const getCombinedViewData=(state)=>state.workkardDetail.combinedView;
    const combinedViewData=yield select(getCombinedViewData);
    const settingsdata = { cvExcludedFilters: payload.notesFilter };
    const newsettingsdata = Object.assign({}, combinedViewData.cvExcludedFilters, settingsdata.cvExcludedFilters);
    var obj= Object.assign({}, combinedViewData, {cvExcludedFilters: newsettingsdata})
    yield put ({
      type: ActionConstants.FETCH_COMBINED_VIEW_DATA_SUCCESS,
      payload: Object.assign({}, combinedViewData, {cvExcludedFilters: newsettingsdata})
    })
    yield call(PUBLIC_API_NOTE_ACTIONS.updateActivityNoteFilters, payload.updateFiltersAPI)
  } catch (error) {
    getAxiosError(error);
  }
}

export default [
  takeLatest(ActionConstants.FETCH_COMBINED_VIEW_DATA_REQUEST, fetchCombinedViewData),
  takeLatest(ActionConstants.FETCH_COMBINED_VIEW_WORKKARD_FILES_REQUEST, fetchCombinedViewWorkkardFiles),
  takeLatest(ActionConstants.UPDATE_COMBINED_VIEW_DATA_REQUEST,updateCombinedViewData),

  takeLatest(ActionConstants.SETTINGS_FILTER_REQUEST,fetchCombinedViewSettingsFilterData),
  takeLatest(ActionConstants.USERS_FILTER_REQUEST,fetchCombinedViewUsersFilterData),
  takeLatest(ActionConstants.ACTIVITY_TITLE_FILTER_REQUEST,fetchCombinedViewActivityTitleFilterData),
  takeLatest(ActionConstants.DATE_FILTER_REQUEST,fetchCombinedViewDateFilterData),

  takeLatest(ActionConstants.SORT_COMBINED_VIEW_DATA_REQUEST,sortCombinedViewData),
  takeLatest(ActionConstants.DOWNLOAD_ALL_FILES, downloadAllFiles),
  takeLatest(ActionConstants.FETCH_PRINT_COMBINED_VIEW_DATA_REQUEST,fetchPrintCombinedViewData),
  takeLatest(ActionConstants.UPDATE_DESELECTED_NOTE_TEMPLATE_COMBINED_VIEW_DATA,updatedeselectednotetemplates),
  takeLatest(ActionConstants.SAVE_DESELECTED_NOTE_TEMPLATE_COMBINED_VIEW_DATA, savedeselectednotetemplates)
  ];
  